<!--
Copyright (C) 2026 Checkmk GmbH - License: Checkmk Enterprise License
This file is part of Checkmk (https://checkmk.com). It is subject to the terms and
conditions defined in the file COPYING, which is part of this source code package.
-->

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, useTemplateRef, watch } from 'vue'

import usei18n from '@/lib/i18n'
import usePersistentRef from '@/lib/usePersistentRef'

import CmkAlertBox from '@/components/CmkAlertBox.vue'
import CmkIcon from '@/components/CmkIcon/CmkIcon.vue'
import CmkMultitoneIcon from '@/components/CmkIcon/CmkMultitoneIcon.vue'
import CmkHeading from '@/components/typography/CmkHeading.vue'

import AiAssistantChat from '@/nonfree/ai/AiAssistantChat.vue'
import AiChatHistory from '@/nonfree/ai/AiChatHistory.vue'
import useAiConversations from '@/nonfree/ai/useAiConversations'

// the toggle and the panel lives in different frames, so we use local storage to sync them:
// Keep in sync with `cmk.aiAssistant` of cmk-frontend.
const OPEN_STORAGE_KEY = 'cmk-ai-assistant-open'
const POSITION_STORAGE_KEY = 'cmk-ai-assistant-position'
const PANEL_SIZE = '300px'

const DOCK_POSITIONS = ['left', 'right', 'bottom'] as const
type DockPosition = (typeof DOCK_POSITIONS)[number]

const { _t } = usei18n()

function parseDockPosition(value: unknown): DockPosition {
  return DOCK_POSITIONS.find((candidate) => candidate === value) ?? 'right'
}

const open = usePersistentRef(OPEN_STORAGE_KEY, false, (value) => value === true, 'session')
const position = usePersistentRef<DockPosition>(
  POSITION_STORAGE_KEY,
  'right',
  parseDockPosition,
  'local'
)

const dockEdges: { position: DockPosition; label: string; rotate: number }[] = [
  { position: 'left', label: _t('Dock to the left'), rotate: 90 },
  { position: 'bottom', label: _t('Dock to the bottom'), rotate: 0 },
  { position: 'right', label: _t('Dock to the right'), rotate: -90 }
]

const view = ref<'chat' | 'history'>('chat')
const chat = useTemplateRef('chat')

const {
  summaries,
  conversation,
  error: loadError,
  refresh,
  startNew,
  open: openConversation,
  remove: removeConversation
} = useAiConversations()

watch(open, (isOpen) => {
  if (isOpen) {
    conversation.value.reconnect()
  } else {
    conversation.value.disconnect()
  }
})

function toggleHistory(): void {
  view.value = view.value === 'history' ? 'chat' : 'history'
}

function newConversation(): void {
  startNew()
  view.value = 'chat'
}

async function showConversation(conversationId: string): Promise<void> {
  if (await openConversation(conversationId)) {
    view.value = 'chat'
  }
}

watch(
  view,
  async (current) => {
    if (current === 'history') {
      await refresh()
    } else {
      chat.value?.focusPrompt()
    }
  },
  { flush: 'post' }
)

function parseStorageValue(value: string | null): unknown {
  return value === null ? null : JSON.parse(value)
}

// e.g. a view in a dashboard iframe, but not the content frame of index.py
function isNestedInIframe(): boolean {
  try {
    return (
      window.parent !== window &&
      !(
        window.parent.location.origin === window.location.origin &&
        window.parent.location.pathname.endsWith('/index.py')
      )
    )
  } catch {
    // accessing the location of a cross-origin parent throws
    return true
  }
}

const renderPanel = !isNestedInIframe()

function onStorage(event: StorageEvent): void {
  if (event.key === OPEN_STORAGE_KEY) {
    open.value = parseStorageValue(event.newValue) === true
  } else if (event.key === POSITION_STORAGE_KEY) {
    position.value = parseDockPosition(parseStorageValue(event.newValue))
  }
}

function onKeydown(event: KeyboardEvent): void {
  if (event.key !== 'Escape' || event.defaultPrevented) {
    return
  }
  if (view.value === 'history') {
    view.value = 'chat'
  } else {
    open.value = false
  }
}

/* Shrinks `#ai-panel-container`, so that the panel does not overlay the page */
function updateMainAreaInset(isOpen: boolean): void {
  const size = isOpen ? PANEL_SIZE : '0px'
  const style = document.documentElement.style
  style.setProperty('--main-area-inset-right', position.value === 'right' ? size : '0px')
  style.setProperty('--main-area-inset-left', position.value === 'left' ? size : '0px')
  style.setProperty('--main-area-inset-bottom', position.value === 'bottom' ? size : '0px')
  // trigger JavaScript layout flow:
  window.dispatchEvent(new Event('resize'))
}

if (renderPanel) {
  watch([open, position], () => updateMainAreaInset(open.value))

  onMounted(() => {
    window.addEventListener('storage', onStorage)
    window.addEventListener('keydown', onKeydown)
    updateMainAreaInset(open.value)
  })

  onBeforeUnmount(() => {
    window.removeEventListener('storage', onStorage)
    window.removeEventListener('keydown', onKeydown)
    updateMainAreaInset(false)
  })
}
</script>

<template>
  <div
    v-if="renderPanel && open"
    class="ai-assistant-panel-app"
    :class="`ai-assistant-panel-app--${position}`"
  >
    <div class="ai-assistant-panel-app__header">
      <div v-if="view === 'history'" class="ai-assistant-panel-app__title">
        <button
          type="button"
          class="ai-assistant-panel-app__action"
          :title="_t('Back to chat')"
          :aria-label="_t('Back to chat')"
          @click="view = 'chat'"
        >
          <CmkMultitoneIcon name="back" primary-color="font" size="xlarge" />
        </button>
        <CmkHeading type="h1">{{ _t('History') }}</CmkHeading>
      </div>
      <span v-else>{{ _t('AI assistant') }}</span>
      <div class="ai-assistant-panel-app__controls">
        <button
          type="button"
          class="ai-assistant-panel-app__action"
          :title="_t('New chat')"
          :aria-label="_t('New chat')"
          @click="newConversation"
        >
          <CmkMultitoneIcon name="chat-new" primary-color="font" size="xlarge" />
        </button>
        <button
          type="button"
          class="ai-assistant-panel-app__action"
          :title="_t('Chat history')"
          :aria-label="_t('Chat history')"
          :aria-pressed="view === 'history'"
          @click="toggleHistory"
        >
          <CmkMultitoneIcon name="chat-history" primary-color="font" size="xlarge" />
        </button>
        <div
          class="ai-assistant-panel-app__dock-group"
          role="group"
          :aria-label="_t('Dock position')"
        >
          <button
            v-for="edge in dockEdges"
            :key="edge.position"
            type="button"
            class="ai-assistant-panel-app__dock"
            :title="edge.label"
            :aria-label="edge.label"
            :aria-pressed="position === edge.position"
            @click="position = edge.position"
          >
            <CmkMultitoneIcon
              name="docking"
              primary-color="font"
              size="small"
              :rotate="edge.rotate"
            />
          </button>
        </div>
        <button
          type="button"
          class="ai-assistant-panel-app__close"
          :title="_t('Close')"
          @click="open = false"
        >
          <CmkIcon :aria-label="_t('Close')" name="close" size="small" />
        </button>
      </div>
    </div>
    <div class="ai-assistant-panel-app__body">
      <CmkAlertBox v-if="loadError" variant="error">{{ loadError }}</CmkAlertBox>
      <AiChatHistory
        v-if="view === 'history'"
        :summaries="summaries"
        :active-id="conversation.id.value"
        @open="showConversation"
        @delete="removeConversation"
        @back="view = 'chat'"
      />
      <AiAssistantChat v-else ref="chat" :conversation="conversation" />
    </div>
  </div>
</template>

<style scoped>
.ai-assistant-panel-app {
  position: fixed;
  inset: 0;
  z-index: 100;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  background-color: var(--default-bg-color);
}

/* The panel fills exactly the inset it reserves in `#ai-panel-container`, so the size is
   only defined once, in `PANEL_SIZE`. */
.ai-assistant-panel-app--right {
  left: auto;
  width: var(--main-area-inset-right);
  border-left: var(--border-width-1) solid var(--border-color-subtle);
}

.ai-assistant-panel-app--left {
  right: auto;
  width: var(--main-area-inset-left);
  border-right: var(--border-width-1) solid var(--border-color-subtle);
}

.ai-assistant-panel-app--bottom {
  top: auto;
  height: var(--main-area-inset-bottom);
  border-top: var(--border-width-1) solid var(--border-color-subtle);
}

.ai-assistant-panel-app__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--dimension-4);
  padding: var(--dimension-6) var(--dimension-6) var(--dimension-4);
}

.ai-assistant-panel-app__title,
.ai-assistant-panel-app__controls {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: var(--dimension-4);
}

.ai-assistant-panel-app__title {
  flex-shrink: 1;
  min-width: 0;

  & > h1 {
    flex-shrink: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.ai-assistant-panel-app__dock-group {
  display: flex;
  align-items: center;
  height: 20px;
}

.ai-assistant-panel-app__action,
.ai-assistant-panel-app__close {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  margin: 0;
  padding: 0;
  border: none;
  background: none;
  color: inherit;
  cursor: pointer;
}

.ai-assistant-panel-app__action:hover {
  opacity: 0.7;
}

.ai-assistant-panel-app__dock {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 16px;
  margin: 0;
  padding: 0;
  border: 1px solid var(--toggle-button-group-border-color);
  background-color: var(--toggle-button-group-inactive-bg-color);
  color: inherit;
  cursor: pointer;

  & + & {
    margin-left: -1px;
  }

  &:first-child {
    border-radius: var(--dimension-2) 0 0 var(--dimension-2);
  }

  &:last-child {
    border-radius: 0 var(--dimension-2) var(--dimension-2) 0;
  }

  &:hover {
    background-color: var(--toggle-button-group-hover-bg-color);
  }

  &[aria-pressed='true'] {
    position: relative;
    z-index: 1;
    width: 28px;
    height: 20px;
    margin-inline: -2px;
    border-color: var(--toggle-button-group-border-color);
    border-radius: var(--dimension-2);
    background-color: var(--toggle-button-group-active-bg-color);
    cursor: default;
  }

  & + &[aria-pressed='true'] {
    margin-left: -3px;
  }
}

.ai-assistant-panel-app__body {
  display: flex;
  flex: 1;
  flex-direction: column;
  overflow: auto;
  padding: var(--dimension-4);
}
</style>
